import React, { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FieldSpec, ParameterConfig } from '@/types/mcpTool';
import { FileJson, Plus, Trash2 } from 'lucide-react';
import { BodyFieldWithOptions } from './BodyFieldWithOptions';

const areCustomFieldsEqual = (
  a: Array<{ key: string; value: any }>,
  b: Array<{ key: string; value: any }>
): boolean => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i].key !== b[i].key) return false;
    if (a[i].value !== b[i].value) return false;
  }
  return true;
};

interface RequestBodyBuilderProps {
  fields: FieldSpec[];
  values: Record<string, any>;
  configs: Record<string, ParameterConfig>;
  onChange: (values: Record<string, any>, configs: Record<string, ParameterConfig>) => void;
  method: 'POST' | 'PUT' | 'PATCH';
  onContextTokenInsert?: (fieldName: string) => void;
}

/**
 * RequestBodyBuilder Component (Form Builder Only)
 *
 * Builds request body for POST/PUT/PATCH requests
 * Features:
 * - Form builder with BodyFieldWithOptions
 * - AI/Human handoff checkboxes
 * - Custom field addition
 * - No JSON mode (removed per user request)
 */
export const RequestBodyBuilder: React.FC<RequestBodyBuilderProps> = ({
  fields,
  values,
  configs,
  onChange,
  method,
  onContextTokenInsert,
}) => {
  const [customFields, setCustomFields] = useState<Array<{ key: string; value: any }>>([]);

  // Rehydrate custom fields from existing values/configs (e.g., when editing an action)
  useEffect(() => {
    setCustomFields((previousFields) => {
      const schemaFieldNames = new Set(fields.map((field) => field.name));
      const candidateKeys = Array.from(
        new Set([
          ...Object.keys(values || {}),
          ...Object.keys(configs || {}),
        ])
      ).filter((key) => !schemaFieldNames.has(key));

      // Keep the current list to preserve in-progress edits (e.g., field name typing)
      const mergedFields = previousFields.map((field) => {
        if (!field.key) {
          return field;
        }
        const hydratedValue = Object.prototype.hasOwnProperty.call(values, field.key)
          ? values[field.key]
          : field.value;
        return hydratedValue === field.value ? field : { ...field, value: hydratedValue };
      });

      const trackedKeys = new Set(
        mergedFields
          .map((field) => field.key)
          .filter(Boolean) as string[]
      );

      candidateKeys.forEach((key) => {
        if (!trackedKeys.has(key)) {
          mergedFields.push({ key, value: values[key] ?? '' });
        }
      });

      const filteredFields = mergedFields.filter((field) => {
        if (!field.key) return true;
        return candidateKeys.includes(field.key);
      });

      return areCustomFieldsEqual(previousFields, filteredFields) ? previousFields : filteredFields;
    });
  }, [fields, values, configs]);

  const handleFieldChange = (fieldName: string, value: any, config: ParameterConfig) => {
    const newValues = { ...values, [fieldName]: value };
    const newConfigs = { ...configs, [fieldName]: config };
    onChange(newValues, newConfigs);
  };

  const handleFieldNameChange = (index: number, newKey: string) => {
    const previousField = customFields[index];
    if (!previousField) {
      return;
    }

    const previousKey = previousField.key;

    if (previousKey === newKey) {
      const updatedFields = [...customFields];
      updatedFields[index] = { ...updatedFields[index], key: newKey };
      setCustomFields(updatedFields);
      return;
    }

    const newValues = { ...values };
    const newConfigs = { ...configs };
    const existingValue = previousKey ? newValues[previousKey] : undefined;
    const existingConfig = previousKey ? newConfigs[previousKey] : undefined;

    if (previousKey) {
      delete newValues[previousKey];
      delete newConfigs[previousKey];
    }

    if (newKey) {
      const initialValue =
        existingValue !== undefined
          ? existingValue
          : Object.prototype.hasOwnProperty.call(newValues, newKey)
            ? newValues[newKey]
            : '';

      if (existingValue !== undefined) {
        newValues[newKey] = existingValue;
      } else {
        newValues[newKey] = initialValue;
      }

      if (existingConfig) {
        newConfigs[newKey] = existingConfig;
      } else if (!newConfigs[newKey]) {
        newConfigs[newKey] = {
          source: 'direct_input',
          fallback: 'error',
          required: false,
          let_ai_decide: false,
          handoff_to_human: false,
        };
      }
    }

    const updatedFields = [...customFields];
    updatedFields[index] = {
      ...updatedFields[index],
      key: newKey,
      value:
        newKey && existingValue !== undefined
          ? existingValue
          : newKey
            ? newValues[newKey]
            : updatedFields[index].value,
    };
    setCustomFields(updatedFields);

    onChange(newValues, newConfigs);
  };

  const addCustomField = () => {
    setCustomFields([...customFields, { key: '', value: '' }]);
  };

  const removeCustomField = (index: number) => {
    const fieldToRemove = customFields[index];
    const newCustomFields = customFields.filter((_, i) => i !== index);
    setCustomFields(newCustomFields);

    if (fieldToRemove.key) {
      const newValues = { ...values };
      const newConfigs = { ...configs };
      delete newValues[fieldToRemove.key];
      delete newConfigs[fieldToRemove.key];
      onChange(newValues, newConfigs);
    }
  };

  const getMethodBadgeColor = () => {
    switch (method) {
      case 'POST': return 'bg-green-100 text-green-700';
      case 'PUT': return 'bg-yellow-100 text-yellow-700';
      case 'PATCH': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Initialize config for predefined fields if not exists
  const getFieldConfig = (fieldName: string): ParameterConfig => {
    return configs[fieldName] || {
      source: 'direct_input',
      fallback: 'error',
      required: false,
      let_ai_decide: false,
      handoff_to_human: false,
    };
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileJson className="h-4 w-4 text-gray-400" />
          <h4 className="text-sm font-medium">Request Body</h4>
          <Badge className={`text-xs ${getMethodBadgeColor()}`}>
            {method}
          </Badge>
        </div>
      </div>

      {/* Help Text */}
      {fields.length === 0 && customFields.length === 0 && (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed">
          <FileJson className="h-12 w-12 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-600 mb-1">No body fields yet</p>
          <p className="text-xs text-gray-500">Click "Add Field" below to start building your request body</p>
        </div>
      )}

      {/* Predefined Fields */}
      {fields.length > 0 && (
        <div className="space-y-3">
          <h5 className="text-xs font-medium text-gray-600">Schema Fields</h5>
          {fields.map((field) => (
            <BodyFieldWithOptions
              key={field.name}
              name={field.name}
              type={field.type}
              value={values[field.name]}
              config={getFieldConfig(field.name)}
              onChange={(value, config) => handleFieldChange(field.name, value, config)}
              placeholder={field.placeholder}
              description={field.description}
              enumOptions={field.enum}
            />
          ))}
        </div>
      )}

      {/* Custom Fields with Options */}
      {customFields.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h5 className="text-xs font-medium text-gray-600">Your Fields</h5>
          </div>
          {customFields.map((field, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Field name (e.g., email)"
                  value={field.key}
                  onChange={(e) => handleFieldNameChange(index, e.target.value)}
                  className="flex-1 font-medium"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeCustomField(index)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              {field.key && (
                <BodyFieldWithOptions
                  name={field.key}
                  type="string"
                  value={values[field.key]}
                  config={getFieldConfig(field.key)}
                  onChange={(value, config) => {
                    handleFieldChange(field.key, value, config);
                    const newFields = [...customFields];
                    newFields[index].value = value;
                    setCustomFields(newFields);
                  }}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Field Button */}
      <Button
        type="button"
        variant="outline"
        size="lg"
        onClick={addCustomField}
        className="w-full"
      >
        <Plus className="h-5 w-5 mr-2" />
        Add Field
      </Button>
    </div>
  );
};
